import React, { createContext, useState, ComponentProps, useMemo, PropsWithChildren } from 'react';
import { createPortal } from 'react-dom';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { Toast } from '../../components/Toast/Toast';

type ToastContextType = [
	ComponentProps<typeof Toast>[],
	React.Dispatch<React.SetStateAction<ComponentProps<typeof Toast>[]>>
];

export const ToastContext = createContext<ToastContextType>([undefined, undefined]);

export function ToastProvider({ children }: PropsWithChildren) {
	const [toasts, setToasts] = useState<ComponentProps<typeof Toast>[]>([]);
	const [autoAnimateTopRight] = useAutoAnimate();
	const [autoAnimateBottomLeft] = useAutoAnimate();

	const topRightToasts = useMemo(() => toasts.filter((t) => t.position === 'top-right'), [toasts]);
	const bottomLeftToasts = useMemo(() => toasts.filter((t) => t.position === 'bottom-left'), [toasts]);

	const value = useMemo<ToastContextType>(() => [toasts, setToasts], [toasts, setToasts]);

	return (
		<ToastContext.Provider value={value}>
			{createPortal(
				<div className="fixed inset-0 z-[var(--z-toast)] w-screen h-screen pointer-events-none">
					{/** Ensure toasts don't overflow the viewport  */}
					<div ref={autoAnimateTopRight} style={{ right: '8px', maxWidth: 'calc(100vw - 8px)' }} className="absolute! flex flex-col items-end gap-2 m-4 inset-0 pointer-events-none *:pointer-events-auto">
						{topRightToasts.map((t) => (
							<Toast
								{...t}
							/>
						))}
					</div>
					{/* The `absolute!` is needed to override auto-animate sometimes setting `position: relative` on the div sometimes for some reason, causing wrong positioning */}
					<div ref={autoAnimateBottomLeft} className="absolute! flex flex-col justify-end items-start gap-2 m-4 left-0 bottom-0 top-0 pointer-events-none *:pointer-events-auto">
						{bottomLeftToasts.map((t) => (
							<Toast
								{...t}
							/>
						))}
					</div>
				</div>,
				document.body,
			)}
			{children}
		</ToastContext.Provider>
	);
}
